import { useEffect, useState } from 'preact/hooks';
import type { GuildView } from '@knight/shared';
import * as S from '../app/state';
import { t } from '../i18n';
import { Btn, Avatar, copyText } from './common';
import { api } from '../net/api';

export function Guild() {
  const acc = S.account.value;
  const [guild, setGuild] = useState<GuildView | null>(null);
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setGuild(await api.guildMe());
      S.account.value = await api.me();
    } catch {
      setGuild(null);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function run(fn: () => Promise<unknown>, okKey?: string) {
    setBusy(true);
    try {
      await fn();
      if (okKey) S.showToast(t(okKey));
      await refresh();
    } catch (e) {
      S.showToast(t('toast.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  const isLeader = guild?.members.find((m) => m.accountId === acc?.id)?.role === 'leader';
  return (
    <div class="screen">
      <div class="card wide panel col tall">
        <div class="row">
          <h2>{t('btn.guild')}</h2>
          <div class="grow" />
          <Btn small onClick={() => (S.screen.value = 'home')}>
            {t('btn.back')}
          </Btn>
        </div>
        {!guild ? (
          <div class="row split" style={{ alignItems: 'flex-start', gap: 24 }}>
            <div class="col" style={{ flex: 1 }}>
              <div class="muted small">{t('label.noGuild')}</div>
              <label class="small muted">{t('label.guildName')}</label>
              <input class="text" value={name} maxLength={24} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
              <label class="small muted">{t('label.guildTag')}</label>
              <input class="text" value={tag} maxLength={4} onInput={(e) => setTag((e.target as HTMLInputElement).value.toUpperCase())} />
              <Btn primary disabled={busy || name.trim().length < 3 || tag.trim().length < 2} onClick={() => void run(() => api.guildCreate(name.trim(), tag.trim()), 'toast.guildCreated')}>
                {t('btn.create')}
              </Btn>
            </div>
            <div class="col" style={{ flex: 1 }}>
              <label class="small muted">{t('label.joinByCode')}</label>
              <div class="row">
                <input class="text grow" value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value.toUpperCase())} />
                <Btn disabled={busy || code.trim().length < 3} onClick={() => void run(() => api.guildJoin(code.trim()), 'toast.guildJoined')}>
                  {t('btn.join')}
                </Btn>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div class="row">
              <h3>
                [{guild.tag}] {guild.name}
              </h3>
              <span class="muted small">
                {t('label.points')}: {guild.points}
              </span>
              <div class="grow" />
              <span class="muted small">{t('label.code')}:</span>
              <b style={{ letterSpacing: '.12em' }}>{guild.code}</b>
              <Btn
                small
                onClick={() => {
                  void copyText(guild.code);
                  S.showToast(t('toast.copied'));
                }}
              >
                {t('btn.copy')}
              </Btn>
            </div>
            <div class="list">
              {guild.members.map((m) => (
                <div class="member">
                  <Avatar i={m.avatar} />
                  <div class="col" style={{ gap: 0 }}>
                    <b>
                      {m.name} {m.role === 'leader' && <span class="gold">★</span>}
                    </b>
                    <span class="small muted">
                      {t('label.fame')} {m.fame} · {m.online ? t('label.online') : t('label.offline')}
                    </span>
                  </div>
                  <div class="grow" />
                  {isLeader && m.accountId !== acc?.id && (
                    <Btn small disabled={busy} onClick={() => void run(() => api.guildKick(m.accountId))}>
                      {t('btn.kick')}
                    </Btn>
                  )}
                </div>
              ))}
            </div>
            <div class="row">
              <div class="grow" />
              <Btn danger disabled={busy} onClick={() => void run(() => api.guildLeave(), 'toast.guildLeft')}>
                {t('btn.leave')}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
